/** German text-to-speech via the browser's speechSynthesis — $0, offline,
 * ships with Chrome/Edge (the test app proved the approach). A paid-TTS
 * upgrade path swaps THIS module only.
 */

export interface SpeakOptions {
  lang?: string;
  /** Playback speed (SpeechSynthesis rate). Defaults to 0.9 — slightly
   * slow, for learners rather than natives. */
  rate?: number;
  /** Fires when the utterance finishes (not when cancelled by a newer
   * one) — the speaking partner uses this to re-arm the microphone. */
  onEnd?: () => void;
  /** Fires as the voice advances, with progress as a 0..1 fraction of the
   * text — the audio player drives its progress bar from this. */
  onBoundary?: (fraction: number) => void;
}

export function ttsAvailable(): boolean {
  return typeof window !== "undefined" && "speechSynthesis" in window;
}

/** Speak `text` in German. Cancels any ongoing utterance first (rapid
 * taps must not queue a backlog). No-ops where unsupported. */
export function speak(text: string, opts: SpeakOptions = {}): boolean {
  if (!ttsAvailable() || !text.trim()) return false;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = opts.lang ?? "de-DE";
  utterance.rate = opts.rate ?? 0.9; // slightly slow — learners, not natives
  const voice = window.speechSynthesis
    .getVoices()
    .find((v) => v.lang.toLowerCase().startsWith("de"));
  if (voice) utterance.voice = voice;
  if (opts.onEnd) utterance.onend = () => opts.onEnd?.();
  if (opts.onBoundary) {
    const total = text.length || 1;
    utterance.onboundary = (e: SpeechSynthesisEvent) =>
      opts.onBoundary?.(Math.min(1, e.charIndex / total));
  }
  window.speechSynthesis.speak(utterance);
  return true;
}

/** Transport controls for the current utterance — no-ops where the
 * browser has no speechSynthesis. The audio player wires these to its
 * pause/resume/restart buttons. */
export function pauseSpeech(): void {
  if (ttsAvailable()) window.speechSynthesis.pause();
}

export function resumeSpeech(): void {
  if (ttsAvailable()) window.speechSynthesis.resume();
}

export function cancelSpeech(): void {
  if (ttsAvailable()) window.speechSynthesis.cancel();
}
